import React, { useState, useEffect } from "react";
import { ShieldCheck, Fingerprint, Lock, Activity, Package, MapPin, Truck, ArrowRight, CreditCard, ChartBar } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function FinanceRetail() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const [verificationStep, setVerificationStep] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setVerificationStep(prev => (prev + 1) % 4);
    }, 1500);
    return () => clearInterval(interval);
  }, []);
  return <section id="finance-retail" ref={ref} className="relative w-full py-24 bg-background overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{
      backgroundImage: 'linear-gradient(currentColor 1px, transparent 1px), linear-gradient(90deg, currentColor 1px, transparent 1px)',
      backgroundSize: '40px 40px'
    }} />
      
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className={`transition-all duration-700 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="max-w-3xl mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium tracking-wider uppercase mb-4">
              <Activity className="w-3 h-3" />
              Industry Solutions
            </div>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-6">
              Security & Scale
            </h2>
            <p className="text-xl text-muted-foreground leading-relaxed">
              Enterprise-grade infrastructure designed for high-stakes environments. 
              We combine military-grade security for financial institutions with 
              massive concurrency for global retail operations.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {}
          <div className={`transition-all duration-700 delay-200 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} data-index={0}>
            <Card className="h-full bg-foreground text-background border-border/20 relative overflow-hidden group">
              {}
              <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-primary/20 blur-[100px] rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" />
              
              <CardHeader className="relative z-10 pb-0">
                <div className="flex justify-between items-start mb-4">
                  <div className="p-3 bg-background/10 rounded-lg border border-white/10 backdrop-blur-sm">
                    <ShieldCheck className="w-8 h-8 text-primary" />
                  </div>
                  <span className="font-mono text-xs text-primary/80 tracking-widest border border-primary/30 px-2 py-1 rounded">
                    SECURE_LAYER_01
                  </span>
                </div>
                <CardTitle className="text-3xl font-bold text-white">Finance</CardTitle>
                <CardDescription className="text-gray-400 text-lg mt-2">
                  Identity Verification & Fraud Prevention
                </CardDescription>
              </CardHeader>

              <CardContent className="relative z-10 pt-8 min-h-[240px] flex flex-col justify-end">
                <div className="space-y-6">
                  <p className="text-gray-300 leading-relaxed">
                    Our voice biometrics engine authenticates users in real-time, 
                    detecting synthetic voices and deepfakes with 99.9% accuracy 
                    before sensitive data is ever accessed.
                  </p>
                  
                  {}
                  <div className="bg-white/5 border border-white/10 rounded-lg p-4 backdrop-blur-md">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-mono text-gray-400">VOICE_PRINT_ANALYSIS</span>
                      <span className="text-xs font-mono text-primary animate-pulse">LIVE</span>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${verificationStep >= 0 ? 'bg-green-500' : 'bg-gray-700'}`} />
                        <span className={`text-sm ${verificationStep >= 0 ? 'text-white' : 'text-gray-600'}`}>Signal Acquisition</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${verificationStep >= 1 ? 'bg-green-500' : 'bg-gray-700'}`} />
                        <span className={`text-sm ${verificationStep >= 1 ? 'text-white' : 'text-gray-600'}`}>Biometric Matching</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${verificationStep >= 2 ? 'bg-green-500' : 'bg-gray-700'}`} />
                        <span className={`text-sm ${verificationStep >= 2 ? 'text-white' : 'text-gray-600'}`}>Liveness Detection</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${verificationStep >= 3 ? 'bg-primary' : 'bg-gray-700'}`} />
                        <span className={`text-sm ${verificationStep >= 3 ? 'text-primary font-bold' : 'text-gray-600'}`}>
                          {verificationStep >= 3 ? 'IDENTITY VERIFIED' : 'Awaiting Confirmation...'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>

              <CardFooter className="relative z-10 border-t border-white/10 pt-6 mt-auto">
                <Link to="/IndustriesPage" className="w-full">
                  <Button variant="outline" className="w-full bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white justify-between group-hover:border-primary/50 transition-colors">
                    Explore Financial Solutions
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </CardFooter>
            </Card>
          </div>

          {}
          <div className={`transition-all duration-700 delay-400 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} data-index={1}>
            <Card className="h-full bg-background border-border shadow-sm relative overflow-hidden group">
              {}
              <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] opacity-50 pointer-events-none" />
              
              <CardHeader className="relative z-10 pb-0">
                <div className="flex justify-between items-start mb-4">
                  <div className="p-3 bg-primary/10 rounded-lg border border-primary/20">
                    <Package className="w-8 h-8 text-primary" />
                  </div>
                  <span className="font-mono text-xs text-muted-foreground tracking-widest border border-border px-2 py-1 rounded bg-background">
                    LOGISTICS_NODE_04
                  </span>
                </div>
                <CardTitle className="text-3xl font-bold text-foreground">Retail</CardTitle>
                <CardDescription className="text-muted-foreground text-lg mt-2">
                  Order Tracking & Automation
                </CardDescription>
              </CardHeader>

              <CardContent className="relative z-10 pt-8 min-h-[240px] flex flex-col justify-end">
                <div className="space-y-6">
                  <p className="text-muted-foreground leading-relaxed">
                    Handle thousands of concurrent order inquiries instantly. 
                    Our AI integrates directly with your OMS to provide real-time 
                    shipping updates, return processing, and inventory checks.
                  </p>

                  {}
                  <div className="bg-background border border-border rounded-lg p-4 shadow-sm">
                    <div className="flex items-center justify-between mb-4 border-b border-border pb-2">
                      <span className="text-xs font-mono text-muted-foreground">ORDER #8492-X</span>
                      <span className="text-xs font-bold text-green-600 flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-600 animate-pulse" />
                        IN TRANSIT
                      </span>
                    </div>
                    <div className="relative pl-4 border-l-2 border-primary/20 space-y-4">
                      <div className="relative">
                        <div className="absolute -left-[21px] top-1 w-3 h-3 rounded-full bg-primary border-2 border-background" />
                        <p className="text-sm font-medium text-foreground">Out for Delivery</p>
                        <p className="text-xs text-muted-foreground">Today, 8:45 AM</p>
                      </div>
                      <div className="relative opacity-60">
                        <div className="absolute -left-[21px] top-1 w-3 h-3 rounded-full bg-muted-foreground/40 border-2 border-background" />
                        <p className="text-sm font-medium text-foreground">Arrived at Facility</p>
                        <p className="text-xs text-muted-foreground">Yesterday, 10:20 PM</p>
                      </div>
                      <div className="relative opacity-40">
                        <div className="absolute -left-[21px] top-1 w-3 h-3 rounded-full bg-muted-foreground/40 border-2 border-background" />
                        <p className="text-sm font-medium text-foreground">Order Processed</p>
                        <p className="text-xs text-muted-foreground">Yesterday, 2:15 PM</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>

              <CardFooter className="relative z-10 border-t border-border pt-6 mt-auto">
                <Link to="/IndustriesPage" className="w-full">
                  <Button className="w-full justify-between group-hover:bg-primary/90 transition-colors">
                    View Retail Use Cases
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </CardFooter>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}